import React, { useCallback, useEffect, useRef, useState } from "react";
import { devWarn } from "../../lib/devLog";
import { api, errText } from "../../lib/api";
import { DispatchMap } from "../../components/DeliveryMap";
import { Star } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STATUS = ["pending", "active", "suspended", "rejected"];

// Small local error boundary so a Google Maps SDK crash on the "Carte live"
// tab never blanks out the whole admin shell. React class-components are
// still the only way to catch descendant render errors.
class MapBoundary extends React.Component {
  constructor(props) { super(props); this.state = { crashed: false }; }
  static getDerivedStateFromError() { return { crashed: true }; }
  componentDidCatch(error, info) {
    devWarn("[MapBoundary] Google Maps threw — falling back to list.", error, info);
  }
  render() {
    if (this.state.crashed) {
      return (
        <p className="card p-4 text-sm text-muted" data-testid="dispatch-map-crashed">
          Carte indisponible pour le moment. Vérifiez la clé Google Maps dans Réglages, ou consultez la liste ci-dessous.
        </p>
      );
    }
    return this.props.children;
  }
}

export function DriverDetail({ id, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [reason, setReason] = useState("");
  const [docMsg, setDocMsg] = useState("");
  const currentId = useRef(id);
  currentId.current = id;
  const load = useCallback((signal) => api.get(`/admin/drivers/${id}`, { signal })
    .then((r) => { if (!signal?.aborted && currentId.current === id) setD(r.data); })
    .catch((e) => { if (!signal?.aborted && currentId.current === id) setDocMsg(errText(e)); }), [id]);
  useEffect(() => {
    const controller = new AbortController();
    setD(null); setDocMsg("");
    load(controller.signal);
    return () => controller.abort();
  }, [load]);
  const act = async (action) => { try { await api.post(`/admin/drivers/${id}/${action}`, { reason }); onChanged(); onClose(); } catch (e) { alert(errText(e)); } };
  const decide = async (doc, status) => {
    setDocMsg("");
    try {
      let note = "";
      if (status === "rejected") {
        const r = window.prompt(`Why is « ${doc.label} » being rejected?\nThe driver gets this by e-mail with a re-upload link.`,
                                doc.note || "Illisible, merci de recharger");
        if (r === null) return;
        note = r || "à recharger";
      }
      await api.post(`/admin/drivers/${id}/documents`, { doc_key: doc.key, status, note });
      setDocMsg(status === "approved" ? "Document approved." : status === "rejected" ? "Document rejected — driver notified." : "Set to pending.");
      await load();
      onChanged?.();
    } catch (e) { setDocMsg(errText(e)); }
  };
  if (!d) return docMsg ? <div role="alert" className="card p-4 text-danger" data-testid="driver-detail-load-error">{docMsg}<button className="btn-ghost text-xs ml-3" data-testid="driver-detail-error-close" onClick={onClose}>Fermer</button></div> : null;
  const isImg = (url) => /\.(jpe?g|png|webp)$/i.test(url || "");
  const docs = d.documents_list || [];
  return (
    <div className="fixed inset-0 scrim z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-plaster max-w-3xl w-full max-h-[90vh] overflow-y-auto p-5 rounded-sm space-y-3" onClick={(e) => e.stopPropagation()} data-testid="admin-driver-detail">
        <div className="flex justify-between items-start"><h3 className="font-display text-xl text-ink">{d.name}</h3><span className="text-xs px-2 py-1 border">{d.status}</span></div>
        <div className="text-sm text-ink space-y-1">
          <p>{d.email} · {d.phone || "—"}</p>
          <p className="text-xs text-muted">{d.driver_type} · {d.city_name} · {(d.zone_names || []).join(", ")}</p>
          <p className="text-xs text-muted">Véhicule : {d.vehicle_type || "—"} · CIN : {d.national_id || "—"} · Immat. : {d.license_plate || "—"}</p>
          <p className="text-xs text-muted">Livraisons : {d.delivered}</p>
        </div>
        <div className="border-t border-brass-light pt-3">
          <p className="smallcaps mb-1">Delivery Partner Agreement</p>
          {d.terms_acceptance ? (
            <div className="text-xs text-ink space-y-0.5 tabular" data-testid="driver-terms-record">
              <p>Accepted <b>{(d.terms_acceptance.accepted_at || "").replace("T", " ").slice(0, 19)} UTC</b></p>
              <p className="text-muted">Version <b>{d.terms_acceptance.version}</b> · Locale <b>{(d.terms_acceptance.locale || "").toUpperCase()}</b></p>
              <p className="text-muted">IP <b>{d.terms_acceptance.ip || "—"}</b></p>
              <p className="text-muted break-all">User agent : {d.terms_acceptance.user_agent || "—"}</p>
            </div>
          ) : (
            <p className="text-xs text-danger" data-testid="driver-terms-missing">No signed agreement on record (legacy driver — must re-accept next login).</p>
          )}
        </div>
        <div className="border-t border-brass-light pt-3">
          <p className="smallcaps mb-1">Documents</p>
          <p className="text-[11px] text-muted mb-2">Click the label to preview. Reject asks for a reason and e-mails the driver a re-upload link.</p>
          {docMsg ? <p className="text-xs text-brass mb-2" data-testid="driver-doc-msg">{docMsg}</p> : null}
          {docs.length === 0 ? <p className="text-sm text-muted">No document on file.</p> : (
            <div className="flex flex-wrap gap-2">
              {docs.map((doc) => (
                <div key={doc.key} className="border border-brass-light p-2 text-[11px] space-y-1 w-52" data-testid={`driver-doc-card-${doc.key}`}>
                  <a href={BACKEND + doc.url} target="_blank" rel="noreferrer" className="underline block truncate" title={doc.label}>{doc.label}</a>
                  {isImg(doc.url) ? (
                    <a href={BACKEND + doc.url} target="_blank" rel="noreferrer" className="block">
                      <img src={BACKEND + doc.url} alt={doc.label} className="w-full h-24 object-cover border border-brass-light" loading="lazy" />
                    </a>
                  ) : (
                    <a href={BACKEND + doc.url} target="_blank" rel="noreferrer" className="block w-full h-24 border border-dashed border-brass-light flex items-center justify-center text-muted">PDF · click to preview</a>
                  )}
                  {doc.note ? <p className="text-danger text-[10px] break-words">Note : {doc.note}</p> : null}
                  <span className={doc.status === "approved" ? "text-success" : doc.status === "rejected" ? "text-danger" : "text-muted"}>{doc.status}</span>
                  <div className="flex gap-2">
                    <button className="underline" data-testid={`driver-doc-approve-${doc.key}`}
                            onClick={() => decide(doc, "approved")}>approve</button>
                    <button className="underline text-danger" data-testid={`driver-doc-reject-${doc.key}`}
                            onClick={() => decide(doc, "rejected")}>reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {d.status === "pending" || d.status === "rejected" ? (
          <input className="field text-sm" placeholder="Motif (si refus)" value={reason} onChange={(e) => setReason(e.target.value)} />
        ) : null}
        <div className="flex flex-wrap gap-2">
          {d.status !== "active" ? <button data-testid="driver-approve" className="btn-primary text-xs" onClick={() => act("approve")}>Approuver</button> : null}
          {d.status === "pending" ? <button data-testid="driver-reject" className="btn-ghost text-xs" onClick={() => act("reject")}>Refuser</button> : null}
          {d.status === "active" ? <button data-testid="driver-suspend" className="btn-ghost text-xs" onClick={() => act("suspend")}>Suspendre</button> : null}
          {d.status === "suspended" ? <button className="btn-primary text-xs" onClick={() => act("reactivate")}>Réactiver</button> : null}
          <button className="btn-ghost text-xs" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

export default function DeliveryNetworkPage() {
  const [tab, setTab] = useState("drivers");
  const [filter, setFilter] = useState({ type: "", status: "" });
  const [drivers, setDrivers] = useState([]);
  const [net, setNet] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [mapsKey, setMapsKey] = useState("");
  const [dispatch, setDispatch] = useState({ drivers: [], deliveries: [], counts: {} });
  const [dispatchMs, setDispatchMs] = useState(() => {
    const raw = localStorage.getItem("fl_dispatch_refresh_ms");
    if (raw === null || raw === "") return 60000;
    const saved = Number(raw);
    return Number.isFinite(saved) && saved >= 0 ? saved : 60000;
  });
  const [dispatchAt, setDispatchAt] = useState(null);
  const [board, setBoard] = useState([]);
  const [alerts, setAlerts] = useState({ alerts: [], count: 0, breached: 0 });
  const [payoutReqs, setPayoutReqs] = useState([]);

  const loadDrivers = () => api.get("/admin/drivers", { params: filter }).then((r) => setDrivers(r.data.drivers)).catch(() => {});
  const loadNet = () => api.get("/admin/delivery-network").then((r) => setNet(r.data)).catch(() => {});
  const loadDispatch = () => api.get("/admin/dispatch").then((r) => { setDispatch(r.data); setDispatchAt(new Date()); }).catch(() => {});
  const loadBoard = () => api.get("/admin/driver-leaderboard").then((r) => setBoard(r.data.leaderboard)).catch(() => {});
  const loadAlerts = () => api.get("/admin/sla-alerts").then((r) => setAlerts(r.data)).catch(() => {});
  const loadPayouts = () => api.get("/admin/driver-payout-requests", { params: { status: "pending" } }).then((r) => setPayoutReqs(r.data.requests)).catch(() => {});
  const markPaid = async (id) => { try { await api.post(`/admin/driver-payout-requests/${id}/mark-paid`); loadPayouts(); } catch (e) { alert(errText(e)); } };
  const exportCsv = () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("fl_token");
    fetch(`${base}/api/admin/driver-payouts.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = "driver-payouts.csv"; a.click(); URL.revokeObjectURL(u); });
  };
  useEffect(() => {
    loadDrivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.type, filter.status]);
  useEffect(() => { loadNet(); loadBoard(); loadAlerts(); loadPayouts(); api.get("/bootstrap").then((r) => setMapsKey(r.data?.maps?.google_maps_api_key || "")).catch(() => {}); const id = setInterval(loadAlerts, 60000); return () => clearInterval(id); }, []);
  useEffect(() => {
    if (tab !== "dispatch") return;
    loadDispatch();
    if (!dispatchMs) return;  // "Off" — manual refresh only
    const id = setInterval(loadDispatch, dispatchMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, dispatchMs]);

  const REFRESH_OPTS = [
    { ms: 15000, label: "15 s" },
    { ms: 30000, label: "30 s" },
    { ms: 60000, label: "60 s" },
    { ms: 120000, label: "2 min" },
    { ms: 300000, label: "5 min" },
    { ms: 0, label: "Manuel" },
  ];
  const changeDispatchMs = (ms) => {
    setDispatchMs(ms);
    localStorage.setItem("fl_dispatch_refresh_ms", String(ms));
  };

  const saveZone = async (zoneId, amount) => { try { await api.put(`/admin/zones/${zoneId}/driver-payout`, { driver_payout_mad: Number(amount) }); loadNet(); } catch (e) { alert(errText(e)); } };

  return (
    <div data-testid="admin-delivery-network">
      <h1 className="font-display text-2xl text-ink mb-1">Delivery network</h1>
      <p className="text-sm text-muted mb-4">Approve network drivers and set the per-zone driver wage. Fleurinelle keeps 100% of the fee on network deliveries.</p>

      {alerts.count > 0 ? (
        <div className="mb-4 border-s-4 border-warning bg-warning/10 p-3 rounded-sm" data-testid="sla-alerts">
          <p className="text-sm text-ink font-medium">⚠ {alerts.count} livraison(s) à risque{alerts.breached ? ` · ${alerts.breached} en retard` : ""}</p>
          <div className="mt-1 space-y-0.5">
            {alerts.alerts.slice(0, 6).map((a) => (
              <p key={a.order_number} className="text-xs text-muted" data-testid={`sla-${a.order_number}`}>
                <b className={a.level === "breached" ? "text-danger" : "text-warning"}>{a.level === "breached" ? "EN RETARD" : "À RISQUE"}</b> · {a.order_number} · {a.recipient_name} · {a.delivery_window_label} · {a.status}{a.minutes_to_deadline <= 0 ? ` (${-a.minutes_to_deadline} min de retard)` : ` (${a.minutes_to_deadline} min restantes)`}
              </p>
            ))}
          </div>
        </div>
      ) : null}
      {net ? <div className="flex gap-3 mb-4 text-sm">
        <span className="card px-3 py-2" data-testid="net-active">Actifs : <b>{net.counts.network_active}</b></span>
        <span className="card px-3 py-2" data-testid="net-pending">En attente : <b>{net.counts.network_pending}</b></span>
        <span className="card px-3 py-2">Livreurs boutique : <b>{net.counts.florist_drivers}</b></span>
      </div> : null}

      <div className="flex gap-2 border-b border-brass-light mb-4">
        <button data-testid="dn-tab-drivers" onClick={() => setTab("drivers")} className={`px-4 py-2 text-sm ${tab === "drivers" ? "text-ink border-b-2 border-brass" : "text-muted"}`}>Livreurs</button>
        <button data-testid="dn-tab-dispatch" onClick={() => setTab("dispatch")} className={`px-4 py-2 text-sm ${tab === "dispatch" ? "text-ink border-b-2 border-brass" : "text-muted"}`}>Carte live</button>
        <button data-testid="dn-tab-board" onClick={() => setTab("board")} className={`px-4 py-2 text-sm ${tab === "board" ? "text-ink border-b-2 border-brass" : "text-muted"}`}>Classement</button>
        <button data-testid="dn-tab-payouts" onClick={() => setTab("payouts")} className={`px-4 py-2 text-sm ${tab === "payouts" ? "text-ink border-b-2 border-brass" : "text-muted"}`}>Versements{payoutReqs.length ? ` (${payoutReqs.length})` : ""}</button>
        <button data-testid="dn-tab-wages" onClick={() => setTab("wages")} className={`px-4 py-2 text-sm ${tab === "wages" ? "text-ink border-b-2 border-brass" : "text-muted"}`}>Rémunération par zone</button>
      </div>

      {tab === "dispatch" ? (
        <div className="space-y-3" data-testid="dn-dispatch">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 justify-between">
            <p className="text-sm text-muted">
              {dispatch.counts?.drivers || 0} livreur(s) en ligne · {dispatch.counts?.deliveries || 0} livraison(s) ouverte(s).
              {dispatchMs
                ? ` Mise à jour toutes les ${REFRESH_OPTS.find((o) => o.ms === dispatchMs)?.label || `${dispatchMs / 1000} s`}.`
                : " Rafraîchissement manuel."}
              {dispatchAt ? <span className="text-muted/70"> Dernière : {dispatchAt.toLocaleTimeString()}.</span> : null}
            </p>
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted flex items-center gap-1.5">
                Rafraîchir
                <select
                  data-testid="dispatch-refresh-select"
                  className="field py-1 px-2 text-xs"
                  value={dispatchMs}
                  onChange={(e) => changeDispatchMs(Number(e.target.value))}
                >
                  {REFRESH_OPTS.map((o) => (
                    <option key={o.ms} value={o.ms}>{o.label}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                data-testid="dispatch-refresh-now"
                onClick={loadDispatch}
                className="bg-ink text-plaster text-xs px-3 py-1.5 rounded-sm hover:bg-ink/90 transition-colors"
              >
                Rafraîchir maintenant
              </button>
            </div>
          </div>
          {mapsKey ? <MapBoundary><DispatchMap apiKey={mapsKey} drivers={dispatch.drivers} deliveries={dispatch.deliveries} /></MapBoundary> : (
            <p className="card p-4 text-sm text-muted" data-testid="dispatch-nokey">Ajoutez la clé Google Maps (Réglages) pour afficher la carte en direct. Liste ci-dessous en attendant.</p>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="card p-3"><p className="smallcaps mb-2">Livreurs en ligne</p>
              {dispatch.drivers.length === 0 ? <p className="text-xs text-muted">Aucun</p> : dispatch.drivers.map((d) => <p key={d.id} className="text-sm text-ink">● {d.name} <span className="text-xs text-muted">({d.driver_type})</span></p>)}
            </div>
            <div className="card p-3"><p className="smallcaps mb-2">Livraisons ouvertes</p>
              {dispatch.deliveries.length === 0 ? <p className="text-xs text-muted">Aucune</p> : dispatch.deliveries.map((o) => <p key={o.order_number} className="text-sm text-ink">🌹 {o.order_number} · {o.recipient_name} <span className="text-xs text-muted">({o.status})</span></p>)}
            </div>
          </div>
        </div>
      ) : null}

      {tab === "board" ? (
        <div className="card divide-y divide-brass-light/50" data-testid="dn-board">
          {board.length === 0 ? <p className="p-4 text-sm text-muted">Aucun livreur réseau.</p> : null}
          {board.map((d, i) => (
            <div key={d.id} className="p-3 flex items-center justify-between" data-testid={`board-row-${d.id}`}>
              <div className="flex items-center gap-3">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${i < 3 ? "bg-ink text-plaster" : "bg-plaster-deep text-ink"}`}>{i + 1}</span>
                <div><p className="text-sm text-ink">{d.name}</p>
                  <p className="text-xs text-muted">{d.delivered} livraisons · {d.on_time_rate != null ? `${Math.round(d.on_time_rate * 100)}% à l'heure` : "—"}</p></div>
              </div>
              <div className="text-right">
                <p className="text-sm text-ink flex items-center gap-1 justify-end"><Star size={13} className="fill-brass text-brass" /> {d.rating != null ? d.rating : "—"} <span className="text-xs text-muted">({d.rating_count})</span></p>
                {d.score != null ? <p className="text-xs text-brass">score {Math.round(d.score * 100)}</p> : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {tab === "drivers" ? (
        <>
          <div className="flex gap-2 mb-3">
            <select className="field w-auto text-sm" data-testid="dn-filter-type" value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })}>
              <option value="">Tous types</option><option value="network">Réseau</option><option value="florist">Boutique</option>
            </select>
            <select className="field w-auto text-sm" data-testid="dn-filter-status" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
              <option value="">Tous statuts</option>{STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="card divide-y divide-brass-light/50">
            {drivers.length === 0 ? <p className="p-4 text-sm text-muted" data-testid="dn-empty">Aucun livreur.</p> : null}
            {drivers.map((d) => (
              <button key={d.id} data-testid={`dn-driver-${d.id}`} onClick={() => setDetailId(d.id)} className="w-full text-left p-3 flex items-center justify-between hover:bg-plaster-deep">
                <div><p className="text-sm text-ink">{d.name} {d.florist_name ? <span className="text-xs text-muted">· {d.florist_name}</span> : null}</p>
                  <p className="text-xs text-muted">{d.email} · {d.city_name} · {d.driver_type}{d.distance_km != null ? ` · ${d.distance_km} km` : ""}</p></div>
                <span className={`text-xs px-2 py-1 border ${d.status === "pending" ? "border-warning text-warning" : d.status === "active" ? "border-ink text-ink" : "border-brass-light text-muted"}`}>{d.status}</span>
              </button>
            ))}
          </div>
        </>
      ) : null}

      {tab === "payouts" ? (
        <div className="space-y-3" data-testid="dn-payouts">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted">{payoutReqs.length} demande(s) de versement en attente.</p>
            <button data-testid="payouts-export-csv" onClick={exportCsv} className="btn-secondary text-xs">Exporter CSV (paie)</button>
          </div>
          <div className="card divide-y divide-brass-light/50">
            {payoutReqs.length === 0 ? <p className="p-4 text-sm text-muted" data-testid="payouts-empty">Aucune demande en attente.</p> : null}
            {payoutReqs.map((r) => (
              <div key={r.id} className="p-3 flex items-center justify-between" data-testid={`payout-req-${r.id}`}>
                <div><p className="text-sm text-ink">{r.driver_name} · <b>{r.amount_mad} MAD</b></p>
                  <p className="text-xs text-muted">{r.count} livraison(s) · demandé le {(r.created_at || "").slice(0, 10)}</p></div>
                <button data-testid={`payout-mark-paid-${r.id}`} onClick={() => markPaid(r.id)} className="btn-primary text-xs">Marquer payé</button>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {tab === "wages" && net ? (
        <div className="space-y-4">
          <p className="text-xs text-muted">Défaut : {net.default_driver_payout_mad} MAD (modifiable dans Réglages).</p>
          {net.cities.map((c) => (
            <div key={c.city_id} className="card p-4" data-testid={`dn-city-${c.city_id}`}>
              <h3 className="smallcaps mb-2">{c.city_name}</h3>
              <div className="space-y-2">
                {c.zones.map((z) => (
                  <div key={z.id} className="flex items-center justify-between gap-3">
                    <span className="text-sm text-ink">{z.name} <span className="text-xs text-muted">· frais {z.delivery_fee_mad} MAD</span></span>
                    <div className="flex items-center gap-1">
                      <input type="number" data-testid={`zone-wage-${z.id}`} defaultValue={z.driver_payout_mad ?? ""} placeholder={net.default_driver_payout_mad}
                             className="field w-20 text-sm text-right" onBlur={(e) => e.target.value !== "" && saveZone(z.id, e.target.value)} />
                      <span className="text-xs text-muted">MAD</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {detailId ? <DriverDetail id={detailId} onClose={() => setDetailId(null)} onChanged={() => { loadDrivers(); loadNet(); }} /> : null}
    </div>
  );
}
